/**
 * Pure game model for the complexity game: typed access to the draft data
 * file, default answers, scoring, and the plain-text checklist. No React in
 * here so the scoring can be exercised from a plain node script.
 *
 * Points, level thresholds and the team-time table are the data file's
 * PROVISIONAL placeholders. Nothing in this file computes or holds a NetSuite
 * subscription figure, and the game no longer reads the benchmark bands.
 */
import raw from '@/components/netsuite-pricing/data/complexity-game.json';
import { STR } from './strings';

/* -------------------------------- Types -------------------------------- */
export type InputType = 'single' | 'multi' | 'toggle' | 'rating';

export interface Anchor {
  text: string;
  source?: string;
  url?: string;
  type?: string;
  display?: string;
}

export interface GameOption {
  id: string;
  label: string;
  complexity_points_PROVISIONAL: number | null;
  published_anchor?: Anchor;
  cost_lines_switched_on?: string[];
  note?: string;
  hk_specific?: boolean;
  /** Branch options only: where the end screen's call to action goes. */
  cta_url?: string;
}

export interface GameInput {
  id: string;
  type: InputType;
  label?: string;
  options: GameOption[];
}

export interface HkFact {
  text: string;
  source?: string;
  url?: string;
  status?: string;
}

export interface GameStep {
  question: string;
  inputs?: GameInput[];
  input?: { id: string; type: 'picker'; per_item_points_PROVISIONAL: Record<string, number> };
  anchor?: Anchor;
  anchors?: Anchor[];
  hk_note?: string;
  hk_facts_for_copy?: HkFact[];
  cost_lines_by_category?: Record<string, string[]>;
}

export interface Integration {
  slug: string;
  name: string;
  category: string;
  weeks_min: number;
  weeks_max: number;
  connector_status: string;
  live_url: string;
  hk_shortlist: boolean;
}

export interface LevelDef {
  level: number;
  name: string;
  points: string;
  timeline_band_weeks?: number[];
  timeline_band_months?: number[];
  timeline_basis: string;
}

export interface Level extends LevelDef {
  /** Lower bound parsed from `points` ("9 to 14" -> 9). */
  min: number;
}

export type Phase = [string, number, number];

export interface TeamTimeRow {
  level: number;
  accountant_share: number;
  finance_manager_share: number;
  months: number;
}

export interface Salary {
  median: number;
  source: string;
  url: string;
}

interface GameData {
  meta: { partner_risk_rules_for_design: string[] };
  flow: { steps: string[]; branch: string };
  steps: Record<string, GameStep>;
  result: {
    complexity_levels_PROVISIONAL: LevelDef[];
    fee_bands_by_level: { status: string };
    phase_split: { use: string; phases: Phase[] };
    rescue_result: { anchor: string; source: string; cta: string; cta_url: string };
    managed_services_result: { source: string; cta_url: string };
    not_in_estimate: string[];
    team_time_PROVISIONAL: {
      checked: string;
      salaries_hkd_per_month: { accountant: Salary; finance_manager: Salary };
      by_level: TeamTimeRow[];
    };
  };
  integrations: Integration[];
}

/* -------------------------------- Data --------------------------------- */
export const DATA = raw as unknown as GameData;

export const RESULT_STEP = 'result';
export const INTEGRATIONS_STEP = 'integrations';
export const START_STEP = 'starting_point';
export const READINESS_STEP = 'readiness';
/** The three-way start lives on the starting_point step. */
export const SITUATION_INPUT = 'situation';
export const USERS_INPUT = 'full_users';

/** Ordered game steps, result excluded. */
export const STEP_IDS: string[] = DATA.flow.steps.filter((s) => s !== RESULT_STEP);

export const LEVELS: Level[] = DATA.result.complexity_levels_PROVISIONAL
  .map((l) => ({ ...l, min: parseInt(l.points, 10) || 0 }))
  .sort((a, b) => a.min - b.min);

/** Top of the meter: the open-ended last level gets a band as wide as the one before it. */
export const METER_MAX: number = (() => {
  const last = LEVELS[LEVELS.length - 1];
  const prev = LEVELS[LEVELS.length - 2];
  return last && prev ? last.min + (last.min - prev.min) : 30;
})();

export const PHASES: Phase[] = DATA.result.phase_split.phases;

const PICKER = DATA.steps[INTEGRATIONS_STEP];
const PER_ITEM_POINTS: Record<string, number> = PICKER.input?.per_item_points_PROVISIONAL ?? {};
const COST_LINES_BY_CATEGORY: Record<string, string[]> = PICKER.cost_lines_by_category ?? {};

/**
 * Pickable integrations. The two "migration (source system)" rows (Xero and
 * QuickBooks) are the starting-point step, not things NetSuite connects to, so
 * they are left out of the picker.
 */
export const INTEGRATIONS: Integration[] = DATA.integrations.filter(
  (i) => !/^n\/a/i.test(i.connector_status)
);
const INTEGRATION_BY_SLUG = new Map(INTEGRATIONS.map((i) => [i.slug, i]));

export function inputsFor(stepId: string): GameInput[] {
  return DATA.steps[stepId]?.inputs ?? [];
}

export function stepQuestion(stepId: string): string {
  return DATA.steps[stepId]?.question ?? '';
}

export function stepData(stepId: string): GameStep | undefined {
  return DATA.steps[stepId];
}

/* ------------------------------- Answers ------------------------------- */
/** Every input stores an array of option ids; single and toggle hold one. */
export type Answers = Record<string, string[]>;

/** The simplest project: first option of every single, "no" on every toggle, the first multi option. */
export function defaultAnswers(): Answers {
  const a: Answers = {};
  for (const stepId of STEP_IDS) {
    for (const input of inputsFor(stepId)) {
      const first = input.options[0];
      if (input.type === 'rating') {
        a[input.id] = [];
      } else if (input.type === 'toggle') {
        const no = input.options.find((o) => o.id === 'no') ?? first;
        a[input.id] = no ? [no.id] : [];
      } else if (input.type === 'multi') {
        a[input.id] = first ? [first.id] : [];
      } else {
        // Start from the simplest project: the first zero-point option, so the
        // meter reads 0 until the visitor adds something.
        const zero = input.options.find((o) => o.complexity_points_PROVISIONAL === 0) ?? first;
        a[input.id] = zero ? [zero.id] : [];
      }
    }
  }
  a[INTEGRATIONS_STEP] = [];
  return a;
}

/** choosing plays the game; live and rescue end on a short screen with a call to action. */
export type Branch = 'choosing' | 'live' | 'rescue';

export function branchOf(answers: Answers): Branch {
  const v = (answers[SITUATION_INPUT] ?? [])[0];
  return v === 'live' || v === 'rescue' ? v : 'choosing';
}

export function isRescue(answers: Answers): boolean {
  return branchOf(answers) === 'rescue';
}

/** The situation option the reader picked, for the branch screens' CTA url. */
export function branchOption(answers: Answers): GameOption | undefined {
  const input = inputsFor(START_STEP).find((i) => i.id === SITUATION_INPUT);
  const id = (answers[SITUATION_INPUT] ?? [])[0];
  return input?.options.find((o) => o.id === id);
}

/** Hand-off from the tier checker: full users to the people step's band. */
export function usersOptionFor(users: number): string {
  if (users <= 10) return 'u10';
  if (users <= 30) return 'u30';
  if (users <= 100) return 'u100';
  return 'u100plus';
}

/* -------------------------------- Scoring ------------------------------ */
export interface Contribution {
  id: string;
  stepId: string;
  label: string;
  points: number;
}

export interface CostLineGroup {
  stepId: string;
  title: string;
  lines: string[];
}

export interface Score {
  points: number;
  level: Level;
  contributions: Contribution[];
  /** The three contributions that added the most, ties broken by step order. */
  top: Contribution[];
  costGroups: CostLineGroup[];
  /** Flat, deduped, in the order they switched on. */
  costLines: string[];
  integrations: Integration[];
  isRescue: boolean;
  branch: Branch;
}

export function levelForPoints(points: number): Level {
  let found = LEVELS[0];
  for (const l of LEVELS) if (points >= l.min) found = l;
  return found;
}

function costLinesForCategory(category: string): string[] {
  if (COST_LINES_BY_CATEGORY[category]) return COST_LINES_BY_CATEGORY[category];
  const key = Object.keys(COST_LINES_BY_CATEGORY).find(
    (k) => k.startsWith(category) || category.startsWith(k)
  );
  return key ? COST_LINES_BY_CATEGORY[key] : [];
}

export function scoreAnswers(answers: Answers): Score {
  const contributions: Contribution[] = [];
  const groups: CostLineGroup[] = [];
  const seen = new Set<string>();
  const flat: string[] = [];

  const addLines = (stepId: string, lines: string[] | undefined): void => {
    if (!lines || lines.length === 0) return;
    let group = groups.find((g) => g.stepId === stepId);
    for (const line of lines) {
      if (seen.has(line)) continue;
      seen.add(line);
      flat.push(line);
      if (!group) {
        group = { stepId, title: STR.stepTitles[stepId] ?? stepId, lines: [] };
        groups.push(group);
      }
      group.lines.push(line);
    }
  };

  let points = 0;
  const branch = branchOf(answers);
  for (const stepId of STEP_IDS) {
    // Only "choosing" plays the game; the other two starts are not scored.
    if (branch !== 'choosing' && stepId !== START_STEP) continue;
    if (stepId === INTEGRATIONS_STEP) {
      for (const slug of answers[INTEGRATIONS_STEP] ?? []) {
        const item = INTEGRATION_BY_SLUG.get(slug);
        if (!item) continue;
        const p = PER_ITEM_POINTS[item.connector_status] ?? 1;
        points += p;
        contributions.push({ id: `integration:${slug}`, stepId, label: item.name, points: p });
        addLines(stepId, costLinesForCategory(item.category));
      }
      continue;
    }
    for (const input of inputsFor(stepId)) {
      if (input.type === 'rating') continue;
      if (branch !== 'choosing' && input.id !== SITUATION_INPUT) continue;
      for (const optionId of answers[input.id] ?? []) {
        const option = input.options.find((o) => o.id === optionId);
        if (!option) continue;
        const p = option.complexity_points_PROVISIONAL ?? 0;
        points += p;
        if (p > 0 || option.cost_lines_switched_on?.length) {
          contributions.push({
            id: `${input.id}:${option.id}`,
            stepId,
            label: input.type === 'toggle' ? (input.label ?? option.label) : option.label,
            points: p,
          });
        }
        addLines(stepId, option.cost_lines_switched_on);
      }
    }
  }

  const top = contributions
    .filter((c) => c.points > 0)
    .map((c, i) => ({ c, i }))
    .sort((a, b) => b.c.points - a.c.points || a.i - b.i)
    .slice(0, 3)
    .map((x) => x.c);

  const integrations = (answers[INTEGRATIONS_STEP] ?? [])
    .map((slug) => INTEGRATION_BY_SLUG.get(slug))
    .filter((i): i is Integration => Boolean(i));

  return {
    points,
    level: levelForPoints(points),
    contributions,
    top,
    costGroups: groups,
    costLines: flat,
    integrations: branch === 'choosing' ? integrations : [],
    isRescue: branch === 'rescue',
    branch,
  };
}

/* ------------------------------- Formatting ---------------------------- */
export function timelineBand(level: Level): string {
  if (level.timeline_band_weeks && level.timeline_band_weeks.length === 2) {
    return STR.result.timelineWeeks(level.timeline_band_weeks[0], level.timeline_band_weeks[1]);
  }
  if (level.timeline_band_months && level.timeline_band_months.length === 2) {
    return STR.result.timelineMonths(level.timeline_band_months[0], level.timeline_band_months[1]);
  }
  return '';
}

/* ----------------------------- Team time ------------------------------ */
export const TEAM_TIME = DATA.result.team_time_PROVISIONAL;

export interface TeamTime extends TeamTimeRow {
  /** Salary cost in HKD, rounded to the nearest thousand. Salary only, MPF excluded. */
  hkd: number;
}

/**
 * The reader's own team's time for a complexity level: arithmetic on the
 * JobsDB monthly medians. Not a PS Global fee and not part of the estimate.
 */
export function teamTime(level: number): TeamTime {
  const rows = TEAM_TIME.by_level;
  const row = rows.find((r) => r.level === level) ?? rows[rows.length - 1];
  const s = TEAM_TIME.salaries_hkd_per_month;
  const raw =
    (row.accountant_share * s.accountant.median + row.finance_manager_share * s.finance_manager.median) * row.months;
  return { ...row, hkd: Math.round(raw / 1000) * 1000 };
}

export function formatHkd(n: number): string {
  return `HK$${n.toLocaleString('en-US')}`;
}

/* ------------------------------ Readiness ------------------------------ */
export type ReadinessTier = 'early' | 'getting_there' | 'ready';

export interface Readiness {
  /** 0 to 100, or null when nothing was rated. */
  score: number | null;
  rated: number;
  total: number;
  tier: ReadinessTier | null;
  /** Input id of the lowest rating (first in order on a tie), or null when all are 4 or 5. */
  weakest: string | null;
}

export function readinessInputs(): GameInput[] {
  return inputsFor(READINESS_STEP).filter((i) => i.type === 'rating');
}

/** Five 1 to 5 self-ratings: all 1s score 0, all 5s score 100. Unrated items are left out. */
export function readinessScore(answers: Answers): Readiness {
  const inputs = readinessInputs();
  const ratings = inputs
    .map((i) => ({ id: i.id, v: parseInt((answers[i.id] ?? [])[0] ?? '', 10) }))
    .filter((r) => r.v >= 1 && r.v <= 5);
  if (ratings.length === 0) return { score: null, rated: 0, total: inputs.length, tier: null, weakest: null };
  const sum = ratings.reduce((t, r) => t + r.v, 0);
  const score = Math.round(((sum - ratings.length) / (4 * ratings.length)) * 100);
  const tier: ReadinessTier = score >= 70 ? 'ready' : score >= 40 ? 'getting_there' : 'early';
  const low = ratings.reduce((m, r) => (r.v < m.v ? r : m), ratings[0]);
  return { score, rated: ratings.length, total: inputs.length, tier, weakest: low.v <= 3 ? low.id : null };
}

function answerLabels(input: GameInput, answers: Answers): string {
  const ids = answers[input.id] ?? [];
  const labels = ids
    .map((id) => input.options.find((o) => o.id === id)?.label)
    .filter((l): l is string => Boolean(l));
  return labels.length ? labels.join(', ') : STR.checklist.none;
}

function labelFor(stepId: string, inputId: string, answers: Answers): string {
  const input = inputsFor(stepId).find((i) => i.id === inputId);
  return input ? answerLabels(input, answers) : STR.checklist.none;
}

/** The page's "ask each partner" questions, with the reader's answer where the game has one. */
export function partnerQuestions(answers: Answers, score: Score, extras: ChecklistExtras = {}): { q: string; yours: string | null }[] {
  const Q = STR.partnerQuestions;
  const banks = score.integrations.filter((i) => /bank/i.test(i.category)).map((i) => i.name);
  const users = labelFor('people', USERS_INPUT, answers);
  const tt = teamTime(score.level.level);
  return [
    { q: Q.users, yours: extras.users ? Q.usersFromChecker(extras.users, users) : users },
    { q: Q.entities, yours: labelFor('structure', 'entities', answers) },
    { q: Q.history, yours: `${labelFor('history', 'history', answers)}; ${Q.fromSystems(labelFor('history', 'legacy_systems', answers))}` },
    { q: Q.banks, yours: banks.length ? banks.join(', ') : Q.noBanks },
    { q: Q.integrations, yours: score.integrations.length ? score.integrations.map((i) => i.name).join(', ') : STR.checklist.none },
    { q: Q.testing, yours: null },
    { q: Q.training, yours: Q.trainingYours(users, labelFor('people', 'training_languages', answers)) },
    { q: Q.support, yours: null },
    { q: Q.teamTime, yours: Q.teamTimeYours(STR.teamTime.assumption(tt.accountant_share, tt.finance_manager_share, tt.months), formatHkd(tt.hkd)) },
  ];
}

export interface ChecklistExtras {
  /** From the tier checker hand-off, when present. */
  users?: number;
  linesPerMonth?: number;
}

/** Plain-text scoping checklist for the lead email. */
export function buildChecklist(answers: Answers, score: Score, extras: ChecklistExtras = {}): string {
  const out: string[] = [STR.checklist.heading, STR.checklist.intro, '', STR.checklist.answers];
  for (const stepId of STEP_IDS) {
    if (stepId === READINESS_STEP) continue;
    const title = STR.stepTitles[stepId] ?? stepId;
    if (stepId === INTEGRATIONS_STEP) {
      const names = score.integrations.map((i) => i.name);
      out.push(`${title}: ${names.length ? names.join(', ') : STR.checklist.none}`);
      continue;
    }
    for (const input of inputsFor(stepId)) {
      const label = input.label ?? STR.inputLabels[input.id] ?? title;
      out.push(`${title}, ${label}: ${answerLabels(input, answers)}`);
    }
  }
  if (extras.linesPerMonth) out.push(STR.checklist.linesPerMonth(extras.linesPerMonth));
  out.push('', STR.checklist.result);
  out.push(STR.checklist.level(score.level.level, score.level.name));
  out.push(`${STR.checklist.timeline}${timelineBand(score.level)} (basis: ${score.level.timeline_basis})`);
  if (score.top.length) {
    out.push(STR.checklist.topDrivers + score.top.map((t) => `${t.label} (+${t.points})`).join('; '));
  }
  if (score.integrations.length) {
    out.push(STR.checklist.integrations);
    for (const i of score.integrations) {
      out.push(`- ${i.name}: ${STR.integrations.weeks(i.weeks_min, i.weeks_max)} (${i.live_url})`);
    }
  } else {
    out.push(STR.checklist.integrationsNone);
  }
  if (score.costGroups.length) {
    out.push(STR.checklist.costLines);
    for (const g of score.costGroups) out.push(`- ${g.title}: ${g.lines.join('; ')}`);
  } else {
    out.push(STR.checklist.costNone);
  }
  const tt = teamTime(score.level.level);
  out.push(STR.checklist.notInEstimate);
  for (const id of DATA.result.not_in_estimate) {
    const row = STR.notInEstimate.rows[id];
    if (!row) continue;
    if (id === 'team_time') {
      out.push(`- ${row.title}: ${STR.teamTime.about(formatHkd(tt.hkd))}. ${STR.teamTime.assumption(tt.accountant_share, tt.finance_manager_share, tt.months)} ${STR.teamTime.salaryOnly}`);
    } else {
      out.push(`- ${row.title}: ${row.body}`);
    }
  }
  out.push(STR.checklist.fee);
  const r = readinessScore(answers);
  if (r.score !== null && r.tier) {
    out.push(STR.checklist.readiness(r.score, STR.readiness.tiers[r.tier]));
    out.push(STR.readiness.next[r.weakest ?? 'all_good']);
  }
  out.push('', STR.checklist.askEachPartner);
  partnerQuestions(answers, score, extras).forEach((pq, i) => {
    out.push(`${i + 1}. ${pq.q}`);
    out.push(`   ${STR.checklist.yourAnswer}${pq.yours ?? STR.checklist.askThem}`);
  });
  return out.join('\n');
}

/** Short summary of what was picked on a step, for the per-step analytics event. */
export function stepAnswerSummary(stepId: string, answers: Answers): string {
  if (stepId === INTEGRATIONS_STEP) return String((answers[INTEGRATIONS_STEP] ?? []).length);
  return inputsFor(stepId)
    .map((i) => `${i.id}=${(answers[i.id] ?? []).join('+') || '-'}`)
    .join(';')
    .slice(0, 200);
}
